import React, { useState } from 'react';
import { HelpCircle, PlayCircle, BookOpen, MessageCircle, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

export const HelpCenter = ({ onStartTour }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const handleStartTour = () => {
    setIsOpen(false);
    onStartTour?.();
  };

  const faqs = [
    {
      question: '¿Cómo cargo movimientos financieros?',
      answer: 'Ve a la sección "Movimientos" y usa el botón "Subir Excel" para importar un archivo con tus transacciones. También puedes agregar movimientos manualmente con el botón "Nuevo Movimiento".'
    },
    {
      question: '¿Cómo priorizo los pagos?',
      answer: 'En la sección "Prioridades", arrastra y suelta los pagos para ordenarlos según su urgencia. Los pagos en la parte superior tienen mayor prioridad.'
    },
    {
      question: '¿Qué muestra el módulo de Rentabilidad?',
      answer: 'El módulo de Rentabilidad analiza la ganancia por cliente, considerando ingresos facturados menos costos de equipo y herramientas asignadas.'
    },
    {
      question: '¿Cómo exporto reportes?',
      answer: 'En cada sección encontrarás botones para exportar a PDF o Excel. Por ejemplo, en Dashboard puedes exportar un resumen ejecutivo en PDF.'
    },
    {
      question: '¿Puedo filtrar por período?',
      answer: 'Sí, la mayoría de las secciones tienen filtros por mes o rango de fechas. Usa los selectores en la parte superior de cada página.'
    }
  ];

  const guideContent = [
    {
      title: 'Dashboard',
      content: 'Tu centro de control financiero. Muestra KPIs de ingresos, egresos y utilidad, junto con gráficos de tendencias y gastos por categoría.'
    },
    {
      title: 'Movimientos',
      content: 'Registro de todas las transacciones financieras. Puedes filtrar por tipo (ingreso/egreso), categoría, cuenta y fechas.'
    },
    {
      title: 'Pagos',
      content: 'Gestiona obligaciones pendientes. Registra pagos con fechas de vencimiento y montos para mantener control de tus compromisos.'
    },
    {
      title: 'Prioridades',
      content: 'Tablero Kanban para organizar pagos. Arrastra las tarjetas para definir qué pagar primero según tu flujo de caja.'
    },
    {
      title: 'Rentabilidad',
      content: 'Análisis detallado de rentabilidad por cliente. Incluye costos de colaboradores, herramientas y financiamientos.'
    },
    {
      title: 'Flujo de Caja',
      content: 'Proyección de entradas y salidas de efectivo. Visualiza tu posición de caja mes a mes.'
    }
  ];

  return (
    <>
      {/* Floating Help Button - positioned above mobile nav and Emergent badge */}
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-36 right-4 z-40 h-12 w-12 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 p-0 md:bottom-8 md:right-6 md:h-14 md:w-14"
        data-testid="help-center-btn"
      >
        <HelpCircle className="w-5 h-5 md:w-6 md:h-6" />
      </Button>

      {/* Main Help Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <HelpCircle className="w-6 h-6 text-blue-600" />
              Centro de Ayuda
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            <button
              onClick={handleStartTour}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-blue-100 hover:border-blue-300 hover:bg-blue-50 transition-all text-left group"
            >
              <div className="p-3 rounded-lg bg-blue-100 group-hover:bg-blue-200 transition">
                <PlayCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Ver tour guiado</h3>
                <p className="text-sm text-slate-500">Repasa las funciones principales</p>
              </div>
            </button>

            <button
              onClick={() => { setShowGuide(true); setIsOpen(false); }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-all text-left group"
            >
              <div className="p-3 rounded-lg bg-slate-100 group-hover:bg-slate-200 transition">
                <BookOpen className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Guía rápida</h3>
                <p className="text-sm text-slate-500">Resumen de cada módulo</p>
              </div>
            </button>

            <button
              onClick={() => { setShowFAQ(true); setIsOpen(false); }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-all text-left group"
            >
              <div className="p-3 rounded-lg bg-slate-100 group-hover:bg-slate-200 transition">
                <MessageCircle className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Preguntas frecuentes</h3>
                <p className="text-sm text-slate-500">Dudas comunes resueltas</p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ Dialog */}
      <Dialog open={showFAQ} onOpenChange={setShowFAQ}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <MessageCircle className="w-6 h-6 text-blue-600" />
              Preguntas Frecuentes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {faqs.map((faq, index) => (
              <div key={index} className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-2">{faq.question}</h4>
                <p className="text-sm text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => { setShowFAQ(false); setIsOpen(true); }}>
              ← Volver
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Guide Dialog */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <BookOpen className="w-6 h-6 text-blue-600" />
              Guía Rápida de la Plataforma
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 mt-4 sm:grid-cols-2">
            {guideContent.map((item, index) => (
              <div key={index} className="p-4 rounded-lg bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200">
                <h4 className="font-semibold text-blue-800 mb-2">{item.title}</h4>
                <p className="text-sm text-slate-600">{item.content}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button variant="outline" onClick={() => { setShowGuide(false); setIsOpen(true); }}>
              ← Volver
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpCenter;
